import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Ban, CheckCircle2, MessageCircle, AlertTriangle } from 'lucide-react';
import { VerifyField } from '../components/VerifyField';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
  { value: 'customer_request', label: 'My plans changed' },
  { value: 'weather', label: 'Weather / unsafe conditions' },
  { value: 'van_issue', label: 'The van / crew is the issue' },
  { value: 'other', label: 'Other' },
];

const WA = 'https://wa.me/447985291097?text=' + encodeURIComponent('Hi Move Van Ltd — I need help with my booking.');

export default function Cancel() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [data, setData] = useState(null);
  const [reason, setReason] = useState('customer_request');
  const [note, setNote] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [verify, setVerify] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/estimate/${id}/public`);
        if (!cancelled) {
          setData(data);
          if (data.cancelled_at) setDone(data.cancellation || { reason_label: 'Cancelled' });
        }
      } catch (e) {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/estimate/${id}/cancel`, {
        reason,
        note: note.trim() || null,
        verify: verify.trim(),
      });
      setDone(data.cancellation || { reason_label: REASONS.find(r => r.value === reason)?.label });
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Something went wrong. Please WhatsApp us.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-[color:var(--mv-orange)]" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <h1 className="font-display text-2xl font-extrabold text-[color:var(--mv-navy)]">Booking not found</h1>
          <p className="text-slate-600 mt-2 text-sm">That link looks invalid or has expired. If you need help, please get in touch.</p>
          <a href={WA} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-4 bg-[#25D366] text-white font-bold px-4 py-2 rounded-md"><MessageCircle className="w-4 h-4" /> WhatsApp us</a>
        </div>
      </div>
    );
  }

  // Already cancelled
  if (done) {
    return (
      <div className="min-h-[60vh] bg-slate-50 py-16 px-4" data-testid="cancel-done">
        <div className="max-w-lg mx-auto bg-white rounded-xl shadow-sm border border-slate-200 p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto">
            <Ban className="w-6 h-6 text-red-600" />
          </div>
          <h1 className="font-display text-2xl font-extrabold text-[color:var(--mv-navy)] mt-3">Booking #{data?.ref} cancelled</h1>
          <p className="text-slate-600 mt-2 text-sm">We've received your cancellation and freed up the slot. Reason: <strong>{done.reason_label}</strong>.</p>
          <p className="text-slate-500 mt-3 text-xs">Deposit is non-refundable, but if you need another move in future we'll be happy to help.</p>
          <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
            <a href={WA} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white font-bold px-4 py-2 rounded-md text-sm"><MessageCircle className="w-4 h-4" /> WhatsApp us</a>
            <Link to="/" className="inline-flex items-center gap-2 border border-slate-200 text-[color:var(--mv-navy)] font-bold px-4 py-2 rounded-md text-sm">Back to home</Link>
          </div>
        </div>
      </div>
    );
  }

  // Confirm step (reason + note)
  if (confirming) {
    return (
      <div className="min-h-[60vh] bg-slate-50 py-16 px-4">
        <form onSubmit={submit} className="max-w-lg mx-auto bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-testid="cancel-form">
          <h1 className="font-display text-2xl font-extrabold text-[color:var(--mv-navy)]">Confirm cancellation</h1>
          <p className="text-slate-600 mt-2 text-sm">Booking <strong>#{data?.ref}</strong> {data?.booking ? <>on <strong>{data.booking.date}</strong> at <strong>{data.booking.time}</strong></> : null} will be cancelled.</p>

          <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mt-5 mb-1">Reason</label>
          <select value={reason} onChange={(e) => setReason(e.target.value)} className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="cancel-reason">
            {REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>

          <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mt-4 mb-1">Note (optional)</label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} rows={3} placeholder="e.g. pipe burst in my flat" className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" data-testid="cancel-note" />

          <VerifyField value={verify} onChange={setVerify} testid="cancel-verify" />
          {err && <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="cancel-error">{err}</div>}

          <div className="mt-5 flex items-center gap-2 justify-end flex-wrap">
            <button type="button" onClick={() => setConfirming(false)} className="h-11 px-4 rounded-md text-sm font-bold text-slate-600 hover:text-slate-900">Back</button>
            <button type="submit" disabled={submitting || !verify.trim()} className="h-11 px-5 rounded-md bg-red-600 disabled:opacity-60 hover:bg-red-700 text-white text-sm font-bold inline-flex items-center gap-2" data-testid="cancel-confirm">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />} Yes, cancel my booking
            </button>
          </div>
        </form>
      </div>
    );
  }

  // Reminder step (no refund)
  return (
    <div className="min-h-[60vh] bg-slate-50 py-16 px-4">
      <div className="max-w-lg mx-auto bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-testid="cancel-reminder">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-700" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-extrabold text-[color:var(--mv-navy)]">Just so you know…</h1>
            <p className="text-slate-600 mt-2 text-sm">
              Deposits are <strong>non-refundable</strong>. If you cancel now, your deposit of <strong>£{data?.deposit}</strong> won't be returned — but your slot will be freed so another customer can book it.
            </p>
            <p className="text-slate-500 mt-3 text-xs">
              Booking <strong>#{data?.ref}</strong>{data?.booking ? <> · <strong>{data.booking.date}</strong> at <strong>{data.booking.time}</strong></> : null}
            </p>
          </div>
        </div>

        <div className="mt-6 rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
          <strong className="text-[color:var(--mv-navy)]">Need to reschedule instead?</strong> Pick a new date — your deposit stays put and we'll move everything for you.
        </div>

        <div className="mt-5 flex items-center gap-2 justify-end flex-wrap">
          <Link to={`/reschedule/${id}`} className="h-11 px-4 rounded-md bg-[color:var(--mv-navy)] hover:bg-[#13315c] text-white text-sm font-bold inline-flex items-center gap-2" data-testid="cancel-reschedule">
            Reschedule instead →
          </Link>
          <a href={WA} target="_blank" rel="noreferrer" className="h-11 px-4 rounded-md bg-[#25D366] text-white text-sm font-bold inline-flex items-center gap-2" data-testid="cancel-whatsapp">
            <MessageCircle className="w-4 h-4" /> WhatsApp us
          </a>
          <button type="button" onClick={() => setConfirming(true)} className="h-11 px-4 rounded-md bg-white border border-red-200 text-red-700 text-sm font-bold inline-flex items-center gap-2" data-testid="cancel-continue">
            <Ban className="w-4 h-4" /> Continue cancelling
          </button>
        </div>
      </div>
    </div>
  );
}
